import React, { FC } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Request, RequestStatus } from "@/types/request";
import {
  AlarmClock,
  ChevronDown,
  ConciergeBell,
  Sparkles,
  User,
  Users,
  ClipboardList,
} from "lucide-react";
import {
  differenceInCalendarDays,
  format,
  isToday,
  isTomorrow,
  isYesterday,
} from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { dashboardNestedCardClassName } from "@/components/pages/dashboard/components/DashboardCard";
import { Badge } from "@/components/ui/badge";
import { getAllowedStatusesForTask } from "@/constants/requestConstants";
import { useUpdateTask } from "@/components/pages/requests/hooks/tasks.hooks";

const D = "requests.dashboard" as const;

interface DashboardRequestItemProps {
  request: Request;
}

const formatCreatedWhenForLine = (dateInput: string | Date, t: TFunction) => {
  const d = new Date(dateInput);
  const time = formatInTimeZone(dateInput, "UTC", "h:mm a");
  if (isToday(d)) {
    return t(`${D}.createdTodayRelative`, { time });
  }
  if (isYesterday(d)) {
    return t(`${D}.createdYesterdayRelative`, { time });
  }
  const daysAgo = differenceInCalendarDays(new Date(), d);
  if (daysAgo === 1) {
    return t(`${D}.createdOneDayAgoAt`, { time });
  }
  if (daysAgo > 1) {
    return t(`${D}.createdDaysAgoRelative`, { count: daysAgo, time });
  }
  return formatInTimeZone(dateInput, "UTC", "MMM d, yyyy, h:mm a");
};

const formatDueWhenForLine = (iso: string, t: TFunction) => {
  const d = new Date(iso);
  const time = formatInTimeZone(iso, "UTC", "h:mm a");
  if (isTomorrow(d)) {
    return t(`${D}.dueTomorrow`, { time });
  }
  if (isToday(d)) {
    return t(`${D}.dueToday`, { time });
  }
  const date = format(d, "MMM d");
  return t(`${D}.dueOtherDate`, { time, date });
};

const serviceIcon = (serviceName: string) => {
  const n = serviceName.toLowerCase();
  if (n.includes("clean")) {
    return Sparkles;
  }
  if (n.includes("custom")) {
    return ClipboardList;
  }
  return ConciergeBell;
};

const translateRequestStatus = (t: TFunction, status: string | undefined) => {
  if (!status) {
    return t(`${D}.statusUnknown`);
  }
  switch (status as RequestStatus) {
    case RequestStatus.APPROVED:
      return t("requests.status.approved");
    case RequestStatus.CANCELED:
      return t("requests.status.cancelled");
    case RequestStatus.TO_DO:
      return t("requests.status.toDo");
    case RequestStatus.IN_PROGRESS:
      return t("requests.status.inProgress");
    case RequestStatus.ON_HOLD:
      return t("requests.status.onHold");
    case RequestStatus.COMPLETED:
      return t("requests.status.completed");
    case RequestStatus.DONE:
      return t("requests.status.done");
    case RequestStatus.NEW_TASK:
      return t("requests.status.newTask");
    default:
      return status;
  }
};

interface RequestStatusSplitBarProps {
  requestId: string;
  taskId?: string;
  currentStatusLabel: string;
  allowed: { value: RequestStatus; label: string }[];
  isActionPending: boolean;
  onSelectStatus: (status: RequestStatus) => void;
  moreActionsLabel: string;
  t: TFunction;
}

const RequestStatusSplitBar: FC<RequestStatusSplitBarProps> = ({
  requestId,
  taskId,
  currentStatusLabel,
  allowed,
  isActionPending,
  onSelectStatus,
  moreActionsLabel,
  t,
}) => {
  const hasMenu = Boolean(taskId) && allowed.length > 0;

  let chevronControl: React.ReactNode;
  if (hasMenu) {
    chevronControl = (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="span"
            size="sm"
            className="h-6 shrink-0 rounded-none border-0 border-l border-slate-500 bg-slate-600 px-2 text-white hover:bg-slate-600/90 disabled:opacity-50"
            disabled={isActionPending}
            aria-label={moreActionsLabel}
          >
            <ChevronDown className="size-4" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {allowed.map((option) => (
            <DropdownMenuItem
              key={option.value}
              variant={
                option.value === RequestStatus.CANCELED
                  ? "destructive"
                  : "default"
              }
              disabled={isActionPending}
              onSelect={() => {
                void onSelectStatus(option.value);
              }}
            >
              {translateRequestStatus(t, option.value)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  } else if (taskId) {
    chevronControl = (
      <Button
        type="button"
        variant="span"
        size="sm"
        className="h-6 shrink-0 rounded-none border-0 border-l border-slate-500 bg-slate-600 px-2 text-white opacity-50"
        disabled
        aria-label={moreActionsLabel}
      >
        <ChevronDown className="size-4" aria-hidden />
      </Button>
    );
  } else {
    chevronControl = (
      <Button
        type="button"
        variant="span"
        size="sm"
        className="h-6 shrink-0 rounded-none border-0 border-l border-slate-500 bg-slate-600 px-2 text-white hover:bg-slate-600/90"
        aria-label={moreActionsLabel}
        asChild
      >
        <Link to={`/requests/${requestId}`}>
          <ChevronDown className="size-4" aria-hidden />
        </Link>
      </Button>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 basis-0 overflow-hidden rounded-lg border border-slate-500">
      {taskId ? (
        <div
          className="flex h-6 min-w-0 flex-1 basis-0 items-center justify-center rounded-none border-0 bg-slate-600 px-2 text-xs font-medium text-white"
          title={currentStatusLabel}
        >
          <span className="truncate">{currentStatusLabel}</span>
        </div>
      ) : (
        <Button
          type="button"
          variant="span"
          size="sm"
          className="h-6 min-w-0 flex-1 basis-0 rounded-none border-0 bg-slate-600 px-2 text-xs font-medium text-white hover:bg-slate-600/90"
          asChild
        >
          <Link
            to={`/requests/${requestId}`}
            className="flex min-w-0 items-center justify-center"
            title={currentStatusLabel}
          >
            <span className="truncate">{currentStatusLabel}</span>
          </Link>
        </Button>
      )}
      {chevronControl}
    </div>
  );
};

const DashboardRequestItem: FC<DashboardRequestItemProps> = ({ request }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const updateTaskMutation = useUpdateTask();
  const profile = request.guest?.profile;
  const guestName = [profile?.firstName, profile?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const displayName = guestName || t(`${D}.guestFallback`);
  const roomNumber = request.booking?.room?.number ?? "—";
  const serviceName = request.service?.name ?? t(`${D}.requestFallback`);
  const description =
    request.description || request.notes || request.service?.description;
  const Icon = serviceIcon(serviceName);
  const task = request.tasks?.[0];
  const staffInitials =
    task?.staff &&
    `${task.staff.profile.firstName?.charAt(0) ?? ""}.${task.staff.profile.lastName ?? ""}`.trim();
  const assignLabel =
    task?.team?.name ??
    (staffInitials && staffInitials !== "."
      ? staffInitials
      : t(`${D}.assignFallback`));
  const AssignIcon = task?.team?.name ? Users : User;

  const allowed =
    task?.id && task.status != null
      ? getAllowedStatusesForTask(String(task.status))
      : [];
  const isActionPending = updateTaskMutation.isPending;

  const runTaskStatus = async (status: RequestStatus) => {
    if (!task?.id) return;
    try {
      await updateTaskMutation.mutateAsync({
        id: task.id,
        data: { status },
      });
      await queryClient.invalidateQueries({
        queryKey: ["dashboard", "guestRequests"],
      });
      toast.success(t(`${D}.statusUpdatedToast`));
    } catch {
      toast.error(t("requests.page.failedToUpdateStatus"));
    }
  };

  const currentStatusLabel = translateRequestStatus(
    t,
    task?.status ?? request.status,
  );

  const createdLine =
    request.createdAt &&
    t(`${D}.createdLine`, {
      when: formatCreatedWhenForLine(request.createdAt, t),
    });

  const expiresLine =
    request.dueDateTime &&
    t(`${D}.expiresLine`, {
      when: formatDueWhenForLine(request.dueDateTime, t),
    });

  return (
    <div className={dashboardNestedCardClassName("flex flex-col gap-3 p-3")}>
      <Link
        to={`/requests/${request.id}`}
        className="flex flex-col gap-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal-glow-3/40"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Badge
              variant="service_gray_sm"
              className="max-h-4 w-fit bg-monochrome-core-2 text-white"
            >
              {roomNumber}
            </Badge>
            <span className="truncate text-xs font-normal text-eathy-comfort-1">
              {displayName}
            </span>
          </div>
          {createdLine ? (
            <time
              className="shrink-0 text-xs font-normal text-primary"
              dateTime={request.createdAt}
            >
              {createdLine}
            </time>
          ) : null}
        </div>
        <div className="flex gap-3">
          <div
            className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-royal-glow-5 text-royal-glow-3"
            aria-hidden
          >
            <Icon className="size-8" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-royal-glow-1">
                {serviceName}
              </span>
              {expiresLine ? (
                <div className="flex max-w-[min(100%,11rem)] items-center justify-end gap-1.5 text-xs font-normal leading-tight text-slate-500">
                  <AlarmClock
                    className="size-3.5 shrink-0 text-slate-500"
                    strokeWidth={2}
                    aria-hidden
                  />
                  <span className="min-w-0 text-right">{expiresLine}</span>
                </div>
              ) : null}
            </div>
            {description ? (
              <p className="line-clamp-2 text-xs font-normal leading-snug text-neutral-500">
                {description}
              </p>
            ) : null}
          </div>
        </div>
      </Link>
      <div className="flex w-full gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-6 min-w-0 flex-1 basis-0 justify-center gap-1.5 border-zinc-200 bg-white text-xs font-normal text-neutral-800"
          asChild
        >
          <Link
            to={`/requests/${request.id}`}
            className="flex min-w-0 items-center justify-center gap-1.5"
          >
            <AssignIcon className="size-3.5 shrink-0" aria-hidden />
            <span className="truncate">{assignLabel}</span>
          </Link>
        </Button>
        <RequestStatusSplitBar
          requestId={request.id}
          taskId={task?.id}
          currentStatusLabel={currentStatusLabel}
          allowed={allowed}
          isActionPending={isActionPending}
          onSelectStatus={runTaskStatus}
          moreActionsLabel={t(`${D}.moreActions`)}
          t={t}
        />
      </div>
    </div>
  );
};

export default DashboardRequestItem;
